/**
 * "Add the ones you picked, or all of them?" — asked before the Add panel opens.
 *
 * WHY IT EXISTS. Compare to Thrive has asked this since Trello #748; Add to
 * Thrive never did. Its whole-dataset lane was reachable only through the
 * table's cross-page banner, and only with NO filter active — which is exactly
 * the state in which the two arms name the same rows. So the one time "all N"
 * was on offer was the one time the choice did not matter, and the rest of the
 * time a recruiter looking at 230 filtered contacts had no way to say "those".
 *
 * WHAT IT SHARES, AND WHAT IT DOES NOT. The rules — when the choice collapses,
 * what happens to a collapsed selection that is over the cap, when the explicit
 * lane must be refused — live in `../imports/scopeChoice.ts`, extracted from
 * `PreCompareConfirmation` rather than re-derived here. Three reviewers had to
 * find the collapsed-over-cap dead end once; nobody should have to find it
 * twice. The copy stays separate (`preAddCopy.ts`): Compare counts rows it has
 * not looked at, Add counts rows it is about to write, and one set of sentences
 * cannot be true of both.
 *
 * THE CAP IS THE SERVER'S. `contact_ids` is capped at `EXPORT_CHUNK_SIZE`
 * (200) and ~204 ids cross the shared ALB WAF's ~8 KB body limit as a bare
 * `TypeError: Failed to fetch`. Add had NO client-side guard before this — the
 * selection simply could not get that big, because nothing encouraged a big
 * one. Offering "all" changes that, so the guard ships with the offer.
 */

import { Button } from "@nu-assist/ui";
import { useEffect, useState } from "react";

import { type ScopeChoice, resolveScopeChoice } from "../imports/scopeChoice";
import {
  PRE_ADD_CANCEL,
  PRE_ADD_NOTHING_TO_SEND,
  PRE_ADD_QUESTION,
  PRE_ADD_RUN,
  PRE_ADD_TITLE,
  preAddAllFilteredLabel,
  preAddAllLabel,
  preAddNotCountedNote,
  preAddScopeExcludedNote,
  preAddSelectedLabel,
  preAddTooManySelected,
} from "./preAddCopy";

/** Add's name for the shared `ScopeChoice`. */
export type PreAddChoice = ScopeChoice;

/**
 * Mirrors `EXPORT_CHUNK_SIZE` on the server (`domain/export.py`), for the same
 * reason `computeCompareScope` mirrors compare's: the client has to refuse an
 * over-cap selection BEFORE the request, and it cannot import a Python
 * constant.
 *
 * NOTHING IN THE TYPE SYSTEM HOLDS THE TWO TOGETHER. `maxItems` reaches
 * `openapi.json` and does not survive into the generated TypeScript — zero
 * occurrences in `packages/contracts/src/openapi.ts` — so a drift produces no
 * compile error in either direction. An earlier version of this comment
 * claimed "a drift is a codegen diff, not a silent divergence", which was the
 * wrong half of a half-truth. What actually holds it is
 * `maxExplicitAddSelection.pin.test.ts`, written for exactly that reason
 * (/pr-review iter 1, Agent C M1).
 */
export const MAX_EXPLICIT_ADD_SELECTION = 200;

export function PreAddConfirmation({
  selectedCount,
  selectedPayloadCount,
  allCount,
  allResolvableCount,
  allScopeTotal,
  notCountedCount,
  filterActive,
  onRun,
  onCancel,
}: {
  /** Rows in the selection that WILL be written (`willBeWritten`). */
  selectedCount: number;
  /**
   * How many ids the selection arm puts ON THE WIRE — the raw selection.
   *
   * Larger than `selectedCount` whenever undecided conflicts are selected:
   * those are subtracted from the promise and still sent, because they carry
   * the per-contact decision. The cap is a limit on the request, so it reads
   * this one. Capping the promise instead let a 210-id body through a 200-id
   * limit (/pr-review iter 1, Agent A R2 / Agent C I1).
   */
  selectedPayloadCount: number;
  /**
   * Rows in the whole scope that will be written — `counts.review.add_writable`
   * under the table's current filters, which is the same set the server
   * resolves for a filtered `select_all`.
   */
  allCount: number;
  /**
   * How many ROWS the "all" arm covers, as the CTA counts them.
   *
   * Larger than `allCount`, which counts WRITES. The CTA that opened this
   * dialog says "all 1300" and the dialog says "Add all 842"; both are right,
   * and a recruiter reading them side by side has no way to know why. Used
   * only to name that gap.
   */
  allScopeTotal: number;
  /**
   * Rows the "all" arm would DISPATCH — `add_resolvable`.
   *
   * What separates the two zero cases, which need opposite answers and used to
   * share one message:
   *
   *   · `resolvable > 0, writable === 0` — rows go and none are written.
   *     Runnable; say so.
   *
   *     NOT "the worker leaves them To review", which this said until
   *     /pr-review iter 7 (Agent A) — the same sentence this card deleted from
   *     three screens, surviving one layer up in the file that deleted it.
   *     `resolve_export_plan` SKIPs a null match type with
   *     `not_compared_no_thrive_check` (`domain/export.py:623-627`), so those
   *     rows come back exactly as they were and nothing is flagged. Only the
   *     undecided-conflict half ends `To review`.
   *   · `resolvable === 0` — nothing to send at all, because everything here is
   *     already in Thrive, dismissed or W1-blocked. The server answers that
   *     with a 409 or a 422, so refuse rather than offer a bounce.
   */
  allResolvableCount: number;
  /** Selected rows that will not be written, named separately instead of
   *  silently dropped. "Blocked" was the old word and it implied a conflict
   *  someone has to clear; half of these are simply uncompared. */
  notCountedCount: number;
  /** Is a membership filter narrowing the table? Changes the "all" wording. */
  filterActive: boolean;
  onRun: (choice: PreAddChoice) => void;
  onCancel: () => void;
}) {
  // NO `hasSelection` PROP. It used to switch this dialog between a question
  // (radios) and a statement (one line), and the statement arm became
  // unreachable when the dialog was narrowed to the single route that asks a
  // real question: `ImportDetailPage` opens it only when `!inAllFilteredMode &&
  // selectedIds.size > 0 && …`, and the prop was `!inAllFilteredMode &&
  // selectedIds.size > 0` — the identical first two conjunctions, so it could
  // only ever arrive `true`.
  //
  // Deleted for the same reason iteration 2 deleted `PRE_ADD_STATEMENT`:
  // unreachable UI that still looks live is a thing the next reader has to
  // disprove. Its JSDoc outlived it inside the props type, above an unrelated
  // field, which is its own small version of the same problem (/pr-review
  // iter 2, Agent A N3).
  //
  // The dialog only opens with a selection in hand, so "these rows" is the
  // arm the recruiter is most likely to want and the one they came from.
  const [choice, setChoice] = useState<PreAddChoice>("selection");

  const { effectiveChoice, selectionTooLarge } = resolveScopeChoice({
    // Always true on this route — the dialog has no other. `resolveScopeChoice`
    // keeps the parameter because Compare's dialog still varies it.
    hasSelection: true,
    // Inert on this path — `countsAreComparable: false` below already forces
    // the collapse off, and `choiceIsNotAChoice` is the only expression that
    // reads either flag. Passed as `true` rather than threaded from the page,
    // because a prop whose value cannot change an outcome is a claim the code
    // does not keep: the page used to pass a real staleness flag here under a
    // comment calling it load-bearing, and a test written to pin it passed
    // identically with the flag inverted. The stale-count window is closed
    // where it is actually reachable — `exportDisabled` on the page
    // (/pr-review iter 2, Agent A N2 / Agent C N6).
    countsAreExact: true,
    // NEVER COLLAPSE. The collapse reasons "equal counts imply equal sets",
    // and Add's two numbers are not produced by the same rule: `selectedCount`
    // is computed client-side over the loaded page — counting unloaded ids as
    // writable and counting conflicts the recruiter has DECIDED — while
    // `allCount` is the server's `add_writable`, which excludes every conflict
    // regardless of decision. Equal numbers can therefore mean different sets,
    // and collapsing would hide a real choice behind a statement.
    //
    // An earlier version passed `countsAreExact: true` here with a comment
    // claiming "both numbers come from the same counts payload". That was
    // simply false — and the comment is quoted rather than deleted because it
    // is the kind of premise that reads plausible and has to be checked
    // against the code, not against another comment (/pr-review iter 1,
    // Agent C I2).
    countsAreComparable: false,
    selectedCount,
    selectedPayloadCount,
    allCount,
    cap: MAX_EXPLICIT_ADD_SELECTION,
    choice,
  });

  // Escape = Cancel, with the same zero side effects as the button.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const allLabel = filterActive ? preAddAllFilteredLabel(allCount) : preAddAllLabel(allCount);
  const conflictNote =
    effectiveChoice === "all"
      ? preAddScopeExcludedNote(
          // Rows the scope holds that a run never sends.
          Math.max(0, allScopeTotal - allResolvableCount),
          // Rows it sends and does not write — the undecided conflicts.
          Math.max(0, allResolvableCount - allCount),
          allScopeTotal
        )
      : preAddNotCountedNote(notCountedCount);
  const tooManyNoteId = "pre-add-too-many-note";
  const nothingNoteId = "pre-add-nothing-note";

  /**
   * "Add all 0 contacts" beside a live button was wrong, and REFUSING the run
   * was the wrong fix for it.
   *
   * Two gates answer different questions and both are satisfied here: the CTA
   * is gated on `exportable_remaining`, which stays positive while unresolved
   * conflicts remain (deliberately — that is the import where the recruiter
   * still has work), and this dialog reads `add_writable`, which excludes them.
   * Reviewers were right that the screen should not offer "all 0" in silence.
   *
   * But the run is NOT a no-op. The worker resolves each undecided conflict to
   * SKIP and the rows come back `to_review` with a reason — a state change the
   * recruiter asked for, and one this repo guards with a regression test
   * (`w2-skip-preserves-contact.spec.ts`, from the #748 bug). Blocking it broke
   * that spec on the E2E suite's first real run against this branch.
   *
   * So: say what will happen, and let it happen. Only the over-cap case blocks,
   * because that one genuinely cannot be sent (/pr-review iter 1 Agent A R3 /
   * Agent C I5; corrected from E2E evidence, iter 2).
   *
   * `nothingToAdd` — the runnable zero — no longer has a note of its own, so it
   * no longer has a variable either. It is described by `conflictNote` above,
   * split by cause, which is both more precise and one sentence rather than
   * two (product owner, 2026-09-16: the screen should be clean). Deleted
   * rather than left computed-and-unread: an unreachable branch that still
   * looks live is what iteration 2 spent a finding on.
   */
  // Only the "all" arm can distinguish the two zeroes; the selection arm's
  // rows were ticked from a list `isExportable` had already narrowed, so its
  // zero always means undecided conflicts.
  const nothingToSend = effectiveChoice === "all" && allResolvableCount === 0;
  const runBlocked = selectionTooLarge || nothingToSend;

  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: the same conditionally-
      // rendered overlay pattern as PreCompareConfirmation — role="dialog" +
      // aria-modal + aria-labelledby is the accessible fit here.
      role="dialog"
      aria-modal="true"
      aria-labelledby="pre-add-title"
      data-testid="pre-add-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="flex w-full max-w-md flex-col rounded-lg border border-border bg-surface shadow-lg">
        <div className="p-5">
          <h2 id="pre-add-title" className="text-h1 text-text">
            {PRE_ADD_TITLE}
          </h2>
          {/* No collapsed arm. Add's two counts are produced by different
              rules (see `countsAreComparable` above), so the dialog can never
              prove the arms name the same rows and always asks. The
              `pre-add-scope-everything` branch that used to live here, with
              `PRE_ADD_STATEMENT` and `preAddEverythingLabel`, was unreachable
              from the moment `countsAreComparable: false` landed — dead UI
              behind a condition that is structurally false. */}
          <p className="mt-1 text-body text-text-secondary">{PRE_ADD_QUESTION}</p>

          <div className="mt-4 flex flex-col gap-2">
            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-border p-3 text-body text-text">
              <span className="flex items-center gap-2.5">
                <input
                  type="radio"
                  name="pre-add-scope"
                  data-testid="pre-add-scope-selected"
                  className="accent-accent"
                  checked={choice === "selection"}
                  onChange={() => setChoice("selection")}
                />
                <span>{preAddSelectedLabel(selectedCount)}</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-border p-3 text-body text-text">
              <span className="flex items-center gap-2.5">
                <input
                  type="radio"
                  name="pre-add-scope"
                  data-testid="pre-add-scope-all"
                  className="accent-accent"
                  checked={choice === "all"}
                  onChange={() => setChoice("all")}
                />
                <span>{allLabel}</span>
              </span>
            </label>
          </div>

          {conflictNote && (
            <p
              data-testid="pre-add-conflict-note"
              className="mt-4 text-caption text-text-secondary"
            >
              {conflictNote}
            </p>
          )}
        </div>

        {selectionTooLarge && (
          <p
            id={tooManyNoteId}
            data-testid="pre-add-too-many"
            className="border-t border-border px-4 pt-3 text-body text-warning-ink"
          >
            {preAddTooManySelected(selectedPayloadCount, MAX_EXPLICIT_ADD_SELECTION)}
          </p>
        )}

        {/* ONLY THE REFUSAL GETS A NOTE. A second one used to render here for
            the RUNNABLE zero — "None of these are counted as additions yet…" —
            and it was removed on the product owner's call (2026-09-16): the
            screen is meant to be clean, and that note was the third sentence
            in a row explaining the same gap.

            Removing it loses nothing, because the note it duplicated is more
            precise: `preAddScopeExcludedNote` above already says "N will be
            sent but not written. Open the contacts table to see why", and
            `preAddNotCountedNote` does the same job on the selection arm.

            THAT QUOTE IS THE CURRENT COPY. It used to read "an unresolved
            conflict, so it comes back flagged To review" and this comment went
            on reproducing that after the sentence itself was corrected
            (/pr-review iter 8, Agent A). A comment that quotes copy is a second
            copy of it, and it drifts the moment the first one changes. That overlap WAS a review
            finding in its own right (iter 3, Agent C I1: the two sentences
            rendered together and contradicted each other).

            What must not go is this one. It explains a button that is
            `aria-disabled`, and a blocked control whose reason is not on
            screen is the defect three reviewers raised in iteration 1. */}
        {nothingToSend && !selectionTooLarge && (
          <p
            id={nothingNoteId}
            data-testid="pre-add-nothing"
            className="border-t border-border px-4 pt-3 text-body text-warning-ink"
          >
            {PRE_ADD_NOTHING_TO_SEND}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border p-4">
          <Button variant="secondary" type="button" data-testid="pre-add-cancel" onClick={onCancel}>
            {PRE_ADD_CANCEL}
          </Button>
          <Button
            type="button"
            data-testid="pre-add-run"
            // `aria-disabled`, NOT `disabled` — the repo's rule, written down at
            // `ImportPicker.tsx`: the `disabled` attribute drops the control out
            // of the tab order and takes the explanation with it, from exactly
            // the users it is for. The button stays focusable, announces the
            // reason through `aria-describedby`, and the handler guards instead.
            aria-disabled={runBlocked || undefined}
            aria-describedby={
              selectionTooLarge ? tooManyNoteId : nothingToSend ? nothingNoteId : undefined
            }
            className={runBlocked ? "opacity-40 cursor-not-allowed" : undefined}
            onClick={() => {
              if (runBlocked) return;
              onRun(effectiveChoice);
            }}
          >
            {PRE_ADD_RUN}
          </Button>
        </div>
      </div>
    </div>
  );
}
